import React from "react";
import { useLanguage } from "../../../../contexts";
import { ASPECT_COLOR, mixHex } from "./visualColors";

const CX = 310, CY = 310;

function pol(r, aDeg) {
  const a = (aDeg * Math.PI) / 180;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}
function P(xy) {
  return xy.map((v) => v.toFixed(1)).join(",");
}
// Arma el "d" de un arco SVG (comando A) — no un "pie slice" cerrado, es un
// stroke con stroke-linecap="round" (por eso los gajos tienen puntas
// redondeadas). `rev` invierte el sentido de dibujo para que el texto sobre
// textPath no quede al revés cuando el gajo cae en la mitad inferior.
function arco(r, a0, a1, rev) {
  const bigArc = a1 - a0 > 180 ? 1 : 0;
  return rev
    ? `M ${P(pol(r, a1))} A ${r} ${r} 0 ${bigArc} 0 ${P(pol(r, a0))}`
    : `M ${P(pol(r, a0))} A ${r} ${r} 0 ${bigArc} 1 ${P(pol(r, a1))}`;
}
// Tamaño de fuente máximo que entra en la longitud de arco disponible.
function fsArc(texto, r, grados, fsMax, factor) {
  const arcLen = (2 * Math.PI * r * grados) / 360;
  return Math.max(8, Math.min(fsMax, arcLen / (Math.max(1, texto.length) * factor)));
}

// "Rueda de estrategia": un gajo SVG por aspecto activo, con el nombre del
// sitio y el horizonte del plan en el centro. Puerto de renderVisionWheel()
// del mockup de referencia (sustemia_objetivos_matriz_estrategia.html).
export function WheelVisual({ groups, baseYear, horizonEnd, siteName }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  if (!groups.length) return null;

  const n = groups.length;
  const paso = 360 / n;
  const gap = Math.max(3.5, 10 - n);

  const nombre = (siteName || "").toUpperCase();
  const fsNombre = nombre.length > 22 ? 11 : nombre.length > 16 ? 13 : 15;

  return (
    <svg viewBox="0 0 620 620" style={{ maxWidth: 520, display: "block", margin: "6px auto 26px" }}>
      <defs>
        <filter id="pd-wshadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="9" floodColor="#1A3562" floodOpacity="0.22" />
        </filter>
        {groups.map(({ aspect }, i) => {
          const a0 = -90 + i * paso + gap, a1 = -90 + (i + 1) * paso - gap;
          const mid = ((a0 + a1) / 2 + 360) % 360;
          const abajo = mid > 10 && mid < 170;
          return (
            <React.Fragment key={aspect}>
              <path id={`pd-wi${i}`} d={arco(abajo ? 208 : 186, a0 + 2, a1 - 2, abajo)} fill="none" />
              <path id={`pd-wo${i}`} d={arco(abajo ? 273 : 262, a0 + 4, a1 - 4, abajo)} fill="none" />
            </React.Fragment>
          );
        })}
      </defs>

      {groups.map(({ aspect, objetivos, avgReduction }, i) => {
        const a0 = -90 + i * paso + gap, a1 = -90 + (i + 1) * paso - gap;
        const mid = ((a0 + a1) / 2 + 360) % 360;
        const abajo = mid > 10 && mid < 170;
        const color = ASPECT_COLOR[aspect] || "#5E89B4";

        // Agrupa por "fuente" (o indicador propio si es sin-KPI) — mismo
        // criterio que porFuente en el mockup, acá basado en o.source
        // directamente en vez de derivarlo de un catálogo de KPIs.
        const porFuente = {};
        objetivos.forEach((o) => {
          const f = (o.source || "").replace(/\s*\(.*\)/, "");
          (porFuente[f] = porFuente[f] || []).push(`−${o.reductionTons}%`);
        });
        const sub = Object.entries(porFuente)
          .map(([f, ps]) => `${f} ${ps.join(" · ")}`)
          .join("  ·  ")
          .toUpperCase();

        const fsA = fsArc(t(aspect), abajo ? 208 : 186, a1 - a0 - 4, 25, 0.92);
        const fsS = fsArc(sub, abajo ? 273 : 262, a1 - a0 - 8, 12, 0.72);

        return (
          <React.Fragment key={aspect}>
            <path d={arco(197, a0, a1, false)} fill="none" stroke={mixHex(color, 0.82)} strokeWidth={72} strokeLinecap="round" />
            <path d={arco(268, a0, a1, false)} fill="none" stroke={color} strokeWidth={50} strokeLinecap="round" />
            <text style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: `${fsA.toFixed(1)}px`, letterSpacing: `${(fsA * 0.16).toFixed(1)}px` }} fill={color}>
              <textPath href={`#pd-wi${i}`} startOffset="50%" textAnchor="middle">{t(aspect).toUpperCase()}</textPath>
            </text>
            <text style={{ fontFamily: "'Inter','DM Sans',sans-serif", fontWeight: 600, fontSize: `${fsS.toFixed(1)}px`, letterSpacing: `${(fsS * 0.08).toFixed(1)}px` }} fill="#fff">
              <textPath href={`#pd-wo${i}`} startOffset="50%" textAnchor="middle">{sub}</textPath>
            </text>
          </React.Fragment>
        );
      })}

      <circle cx={CX} cy={CY} r={118} fill="#ffffff" filter="url(#pd-wshadow)" />
      <circle cx={CX} cy={CY} r={104} fill="#1A3562" />
      <circle cx={CX} cy={CY} r={112} fill="none" stroke="#C9D9E8" strokeWidth={1.2} strokeDasharray="2 6" />
      <text x={CX} y={CY - 26} textAnchor="middle" style={{ fontFamily: "'Inter',sans-serif", fontWeight: 600, fontSize: `${fsNombre}px`, letterSpacing: "1.5px" }} fill="#ffffff">
        {nombre}
      </text>
      <line x1={CX - 42} y1={CY - 11} x2={CX + 42} y2={CY - 11} stroke="#5E89B4" strokeWidth={1} />
      <text x={CX} y={CY + 13} textAnchor="middle" style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "15px", letterSpacing: "3px" }} fill="#A8C4DC">
        {t("pd_wheel_plan_label")}
      </text>
      <text x={CX} y={CY + 41} textAnchor="middle" style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: "23px" }} fill="#ffffff">
        {baseYear}–{horizonEnd}
      </text>
    </svg>
  );
}
